/* eslint-disable no-const-assign */
/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

const TooltipWrapper = styled('div')`
   ::after {
    content: "";
    position: absolute;
    left: 50%;
    right: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: ${(props) => (props.isTooltipBottom
    ? `transparent transparent ${props.backgroundColor || 'rgba(68, 68, 68, 0.6)'} transparent`
    : `${props.backgroundColor || 'rgba(68, 68, 68, 0.6)'} transparent transparent transparent`)};
    top: ${(props) => (props.isTooltipBottom ? '-10px' : `${props.toolTipWrapperHeight}px`)};
  }

  ::before {
    content: "";
    position: absolute;
    left: 50%;
    right: 50%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: ${(props) => (props.isTooltipBottom
    ? `transparent transparent ${props.shadowColor || 'rgba(0, 0, 0, 0.1)'} transparent`
    : `${props.shadowColor || 'rgba(0, 0, 0, 0.1)'} transparent transparent transparent`)};
    top: ${(props) => (props.isTooltipBottom ? '-12px' : `${props.toolTipWrapperHeight + 2}px`)};
    z-index: 0;
  }
}
`;

const TBportalTooltip = ({
  children,
  targetRef,
  backgroundColor,
  setShowToolTip,
  closeOnResize,
  closeOnScroll,
  allowBottomPosition,
  scrollContainerRefTopPosition,
}) => {
  const toolTipWrapperRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [isTooltipBottom, setIsTooltipBottom] = useState(false);
  const calendarContainerScrollPosition = useSelector((state) => state.calendar.data.calendarContainerScrollPosition);
  const closeToolTip = () => setShowToolTip && setShowToolTip(false);
  const updateTooltipPosition = () => {
    if (targetRef.current && toolTipWrapperRef?.current) {
      const rect = targetRef.current.getBoundingClientRect();
      const toolTipWrapperHeight = toolTipWrapperRef.current.getBoundingClientRect().height;
      const toolTipWrapperWidth = toolTipWrapperRef.current.getBoundingClientRect().width;

      const topPosition = rect.top + window.scrollY - toolTipWrapperHeight - 20;
      const bottomPosition = rect.top + window.scrollY + rect.height + 10;

      /** if the top of the tool tip ref component is out side of the scrollview or
       * window (default) then we wnat to close the tool tip
       */
      if (rect.top < scrollContainerRefTopPosition && setShowToolTip) {
        closeToolTip();
      }
      if (allowBottomPosition) {
      /** Check if tooltip is getting cut off by the top of window */
        if (topPosition < 0) {
          topPosition = bottomPosition;
          setIsTooltipBottom(true); /** Tooltip is below the target */
        } else {
          setIsTooltipBottom(false); /** Tooltip is above the target */
        }
      }

      setTooltipPosition({
        top: topPosition,
        left: rect.left + window.scrollX - (toolTipWrapperWidth / 2) + (rect.width / 2),
      });
    }
  };

  useEffect(() => {
    /** Update tooltip position when component mounts and when scroll or resize happens */
    updateTooltipPosition();
    if (closeOnScroll) {
      window.addEventListener('scroll', closeToolTip, true);
    } else {
      window.addEventListener('scroll', updateTooltipPosition, true);
    }
    if (closeOnResize) {
      window.addEventListener('resize', closeToolTip);
    } else {
      window.addEventListener('resize', updateTooltipPosition);
    }

    return () => {
      window.removeEventListener('scroll', updateTooltipPosition, true);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [targetRef, calendarContainerScrollPosition]);

  return createPortal(
    <div style={{
      position: 'absolute',
      top: tooltipPosition?.top,
      left: tooltipPosition?.left,
      zIndex: 9999,
      color: 'white',
      padding: '5px',
      borderRadius: '4px',
      backgroundColor: backgroundColor || 'white',
      boxShadow: isTooltipBottom
        ? 'rgba(0, 0, 0, 0.1) 0px 0px 4px 0px' /** Flip the shadow upwards */
        : '0 2px 4px 0 rgba(0, 0, 0, 0.1)', /** Default shadow downwards */
    }}
    >
      <TooltipWrapper
        ref={toolTipWrapperRef}
        backgroundColor={backgroundColor}
        toolTipWrapperHeight={toolTipWrapperRef?.current?.getBoundingClientRect().height + 10}
        isTooltipBottom={isTooltipBottom}
      >
        {children}
      </TooltipWrapper>
    </div>,
    document.body, /** Portal renders the tooltip into the body */
  );
};

TBportalTooltip.propTypes = {
  children: PropTypes.node.isRequired,
  targetRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
  scrollContainerRefTopPosition: PropTypes.number,
  setShowToolTip: PropTypes.func.isRequired,
  /** allow bottom position mean that you want the tool tip to flip to the bottom
   * of the ref if the tool tip is getting cut off by the top of the page and the ref is still visible
   */
  allowBottomPosition: PropTypes.bool,
};

TBportalTooltip.defaultProps = {
  backgroundColor: 'white',
  closeOnResize: null,
  closeOnScroll: null,
  scrollContainerRefTopPosition: 0,
  allowBottomPosition: false,
};
export default TBportalTooltip;
